// The rule for a new password: long enough and not guessable. There is no "one capital,
// one number, one symbol" rule on purpose: it lets "Password1!" through and stops a long
// sentence. The server can only check the length (Supabase's minimum), so the rest is
// checked here, on sign-up and on the reset-password screen.

export const PASSWORD_MIN_LENGTH = 10

export type PasswordLevel = 'empty' | 'short' | 'guessable' | 'ok' | 'strong'

export interface PasswordCheck {
  /** 10 characters or more. */
  long: boolean
  /** Is more than the person's name or the first part of their email. */
  personal: boolean
  /** Not a password people often use, and not a plain pattern. */
  common: boolean
  level: PasswordLevel
  /** How many of the bar's four segments are lit. */
  bars: 0 | 1 | 2 | 3 | 4
  /** The word beside the bar ("Strong"); empty before anything is typed. */
  word: string
  /** Why it is refused, in a line; null when it is allowed. */
  reason: string | null
  ok: boolean
}

// Long enough to pass the length check and still among the first a guesser tries.
const COMMON = new Set([
  'passwordpassword',
  'password1234',
  'iloveyou123',
  'iloveyou1234',
  'qwertyuiop',
  'qwertyuiop123',
  'qwerty12345',
  'qwerty123456',
  '1q2w3e4r5t',
  '1q2w3e4r5t6y',
  'q1w2e3r4t5',
  '1qaz2wsx3edc',
  'asdfghjkl123',
  'zxcvbnm123',
  'abcdefghij',
  'abcd123456',
  'abc1234567',
  'letmein12345',
  'welcome12345',
  'changeme123',
  'sanogodam123',
  'kathmandu123',
])
// A word like these with only digits or symbols after it ("password123", "nepal@2083").
const WORDS = [
  'password',
  'passwd',
  'qwerty',
  'iloveyou',
  'welcome',
  'letmein',
  'admin',
  'administrator',
  'nepal',
  'kathmandu',
  'sanogodam',
  'godam',
  'test',
  'changeme',
  'secret',
  'monkey',
  'dragon',
  'football',
]
const RUNS = [
  '01234567890123456789',
  '98765432109876543210',
  'abcdefghijklmnopqrstuvwxyz',
  'zyxwvutsrqponmlkjihgfedcba',
  'qwertyuiopasdfghjklzxcvbnm',
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
]

const plain = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')

/** One or two characters repeated ("aaaaaaaaaa", "1212121212"). */
function repeats(text: string): boolean {
  return /^(.{1,2})\1+.?$/.test(text)
}

function isCommon(password: string): boolean {
  const lower = password.toLowerCase()
  const bare = plain(password)
  if (COMMON.has(lower) || COMMON.has(bare)) return true
  if (repeats(lower) || repeats(bare)) return true
  if (bare.length >= 6 && RUNS.some((run) => run.includes(bare))) return true
  // The word, doubled or not, with nothing but digits around it.
  const letters = bare.replace(/\d+/g, '')
  return WORDS.some((word) => letters === word || letters === word + word)
}

/** The whole name, its words and the email's first part, longest first. */
function personalParts(name: string, email: string): string[] {
  const parts = [plain(name), plain(email.split('@')[0] ?? ''), ...name.split(/\s+/).map(plain)]
  return [...new Set(parts)].filter((part) => part.length >= 4).sort((a, b) => b.length - a.length)
}

/**
 * A name inside a long sentence is fine ("sita likes blue tea"); a password that is
 * little more than the name is not ("sitasharma99"). So: take the name and the email
 * out, and at least six characters must be left.
 */
function isPersonal(password: string, name: string, email: string): boolean {
  let rest = plain(password)
  const before = rest.length
  for (const part of personalParts(name, email)) rest = rest.split(part).join('')
  return rest.length < before && rest.length < 6
}

export function checkPassword(
  password: string,
  who: { name?: string; email?: string } = {},
): PasswordCheck {
  const long = password.length >= PASSWORD_MIN_LENGTH
  const personal = !isPersonal(password, who.name ?? '', who.email ?? '')
  const common = !isCommon(password)
  const done = { long, personal, common }
  if (!password)
    return { ...done, level: 'empty', bars: 0, word: '', reason: null, ok: false, long: false }
  if (!long) {
    const more = PASSWORD_MIN_LENGTH - password.length
    return {
      ...done,
      level: 'short',
      bars: 1,
      word: 'Too short',
      reason: `${more} more character${more === 1 ? '' : 's'}.`,
      ok: false,
    }
  }
  if (!common || !personal) {
    return {
      ...done,
      level: 'guessable',
      bars: 1,
      word: 'Easy to guess',
      reason: common
        ? 'It has your name or email in it. Pick something only you would think of.'
        : 'Many people use this one. Pick something only you would think of.',
      ok: false,
    }
  }
  // Longer, or with more than one kind of character, is harder to guess.
  const kinds = [/\p{Ll}/u, /\p{Lu}/u, /\d/, /[^\p{L}\p{N}]/u].filter((k) => k.test(password))
  const strong = password.length >= 14 || (password.length >= 12 && kinds.length >= 3)
  return {
    ...done,
    level: strong ? 'strong' : 'ok',
    bars: strong ? 4 : 3,
    word: strong ? 'Strong' : 'Good',
    reason: null,
    ok: true,
  }
}

/** What `PasswordStrength` shows for a check: the bar, its word and the three lines. */
export function strengthOf(check: PasswordCheck) {
  const typed = check.level !== 'empty'
  const state = (met: boolean) => (!typed ? 'wait' : met ? 'yes' : 'no') as 'wait' | 'yes' | 'no'
  return {
    bars: check.bars,
    word: check.word,
    tone: (check.ok ? 'success' : 'danger') as 'success' | 'danger',
    reason: check.reason,
    checks: [
      { label: `${PASSWORD_MIN_LENGTH} characters or more`, state: state(check.long) },
      { label: 'Not just your name or your email', state: state(check.personal) },
      { label: 'Not a password people often use', state: state(check.common) },
    ],
  }
}
